import { useMemo, useState } from 'react';

import {
  getSearchedLocation,
  getSearchLocationForBriefLocation,
  vendorSearchUrl,
} from '@zola-helpers/client/dist/es/marketplace/vendorSearchUtils';
import {
  VENUES_TAXONOMY_KEY,
  BAR_SERVICES_TAXONOMY_KEY,
  BANDS_DJS_TAXONOMY_KEY,
  CAKES_DESSERTS_TAXONOMY_KEY,
  CATERING_TAXONOMY_KEY,
  FLORISTS_TAXONOMY_KEY,
  HAIR_MAKEUP_TAXONOMY_KEY,
  PHOTOGRAPHERS_TAXONOMY_KEY,
  VIDEOGRAPHERS_TAXONOMY_KEY,
  PLANNERS_TAXONOMY_KEY,
  SearchableVendorTaxonomyKey,
  VendorTaxonomyKey,
  SEARCHABLE_VENDOR_TAXONOMY_KEYS,
  OFFICIANTS_TAXONOMY_KEY,
  EXTRAS_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorTaxonomySwitchFunc } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import { getVendorWordPill } from '@zola-helpers/client/dist/es/marketplace/vendorWords';
import { isDesktopV2 } from '@zola-helpers/client/dist/es/util/responsive';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import { PencilIcon } from '@zola/zola-ui/src/components/SvgIconsV3/Pencil';
import TagV2, { TagV2Variant } from '@zola/zola-ui/src/components/Tag/TagV2';
import VendorCircleIcon from '@zola/zola-ui/src/components/VendorCircleIcon/VendorCircleIcon';
import BandsDjsTile from '@zola/zola-ui/src/components/VendorsCategoryTiles/assets/bands-djs-tile.webp';
import BarServicesTile from '@zola/zola-ui/src/components/VendorsCategoryTiles/assets/bar-services-tile.webp';
import CakesDessertsTile from '@zola/zola-ui/src/components/VendorsCategoryTiles/assets/cakes-desserts-tile.webp';
import CateringTile from '@zola/zola-ui/src/components/VendorsCategoryTiles/assets/catering-tile.webp';
import ExtrasTile from '@zola/zola-ui/src/components/VendorsCategoryTiles/assets/extras-tile.webp';
import FloristsTile from '@zola/zola-ui/src/components/VendorsCategoryTiles/assets/florists-tile.webp';
import HairMakeupTile from '@zola/zola-ui/src/components/VendorsCategoryTiles/assets/hair-makeup-tile.webp';
import OfficiantsTile from '@zola/zola-ui/src/components/VendorsCategoryTiles/assets/officiants-tile.webp';
import PhotographersTile from '@zola/zola-ui/src/components/VendorsCategoryTiles/assets/photographers-tile.webp';
import PlannersTile from '@zola/zola-ui/src/components/VendorsCategoryTiles/assets/planners-tile.webp';
import VenuesTile from '@zola/zola-ui/src/components/VendorsCategoryTiles/assets/venues-tile.webp';
import VideographersTile from '@zola/zola-ui/src/components/VendorsCategoryTiles/assets/videographers-tile.webp';
import { useResponsiveDesign } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignContext';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';
import { Title2 } from '@zola/zola-ui/src/typography/Headings';

import cx from 'classnames';

import { useSearchedLocation } from '~/contexts/SearchContext';
import { useUserContext } from '~/contexts/UserContext';
import useWeddingLocation from '~/hooks/useWeddingLocation';
import { getBriefLocation } from '~/pages/couples/explore/util/helpers';
import { LocationOrAllMarketsType } from '~/types/responseTypes';
import featureFlags from '~/util/featureFlags';
import { isGuest } from '~/util/userContextUtils';
import { vendorCategoryLandingUrl } from '~/util/vendorSearchUrl';

import styles from './vendorsByCategory.module.less';

const getOrderedVendorCategoryKeys = () => {
  const orderedVendorCategoryKeys: SearchableVendorTaxonomyKey[] = [
    VENUES_TAXONOMY_KEY,
    PHOTOGRAPHERS_TAXONOMY_KEY,
    VIDEOGRAPHERS_TAXONOMY_KEY,
    FLORISTS_TAXONOMY_KEY,
    HAIR_MAKEUP_TAXONOMY_KEY,
    PLANNERS_TAXONOMY_KEY,
    BANDS_DJS_TAXONOMY_KEY,
    CATERING_TAXONOMY_KEY,
    CAKES_DESSERTS_TAXONOMY_KEY,
    BAR_SERVICES_TAXONOMY_KEY,
  ];

  if (featureFlags.get('enableOfficiantsAndExtras')) {
    orderedVendorCategoryKeys.push(OFFICIANTS_TAXONOMY_KEY, EXTRAS_TAXONOMY_KEY);
  }

  return orderedVendorCategoryKeys;
};

export const ORDERED_VENDOR_CATEGORY_KEYS: SearchableVendorTaxonomyKey[] =
  getOrderedVendorCategoryKeys();

type VendorsByCategoryProps = {
  className?: string;
  excludeCategoryByType?: VendorTaxonomyKey;
  showModal?: () => void;
  isClpTest?: boolean;
  /** Show image and hide icon at all breakpoints, not just mobile and tablet */
  isClpTestAllBreakpoints?: boolean;
  hideHed?: boolean;
  useLastSearchedLocation?: boolean;
};

type VendorsCategoryListProps = React.HTMLAttributes<HTMLDivElement> & {
  categories?: SearchableVendorTaxonomyKey[];
  openInNewTab?: boolean;
  onLinkClick?: () => void | null;
  isClpTest?: boolean;
  isClpTestAllBreakpoints?: boolean;
  useLastSearchedLocation?: boolean;
};

const mapVendorKeyToTile = (vendorTaxonomyKey: SearchableVendorTaxonomyKey) =>
  getVendorTaxonomySwitchFunc({
    [BANDS_DJS_TAXONOMY_KEY]: BandsDjsTile,
    [BAR_SERVICES_TAXONOMY_KEY]: BarServicesTile,
    [CAKES_DESSERTS_TAXONOMY_KEY]: CakesDessertsTile,
    [CATERING_TAXONOMY_KEY]: CateringTile,
    [EXTRAS_TAXONOMY_KEY]: ExtrasTile,
    [FLORISTS_TAXONOMY_KEY]: FloristsTile,
    [HAIR_MAKEUP_TAXONOMY_KEY]: HairMakeupTile,
    [OFFICIANTS_TAXONOMY_KEY]: OfficiantsTile,
    [PHOTOGRAPHERS_TAXONOMY_KEY]: PhotographersTile,
    [PLANNERS_TAXONOMY_KEY]: PlannersTile,
    [VENUES_TAXONOMY_KEY]: VenuesTile,
    [VIDEOGRAPHERS_TAXONOMY_KEY]: VideographersTile,
  })(vendorTaxonomyKey);

export const VendorsCategoryList = ({
  categories = SEARCHABLE_VENDOR_TAXONOMY_KEYS,
  className,
  isClpTestAllBreakpoints = false,
  isClpTest = isClpTestAllBreakpoints,
  openInNewTab = false,
  onLinkClick = () => null,
  useLastSearchedLocation = false,
  ...rest
}: VendorsCategoryListProps): JSX.Element => {
  const userContext = useUserContext();
  const { weddingLocation } = useWeddingLocation();
  const { searchedLocation } = useSearchedLocation();

  const [lastSearchedLocation, setLastSearchedLocation] =
    useState<LocationOrAllMarketsType | null>();

  useEffectOnce(() => {
    setLastSearchedLocation(getSearchedLocation());
  });

  const shouldSkipLP = useMemo(() => {
    return (
      !isGuest(userContext) &&
      typeof window !== 'undefined' &&
      isDesktopV2() &&
      userContext?.marketplace_account?.has_inquired
    );
  }, [userContext]);

  const weddingLocationForSRP = useMemo(() => {
    return getSearchLocationForBriefLocation(getBriefLocation(weddingLocation));
  }, [weddingLocation]);

  const locationToUse = useMemo(() => {
    return shouldSkipLP && useLastSearchedLocation
      ? lastSearchedLocation || searchedLocation || weddingLocationForSRP
      : searchedLocation;
  }, [
    shouldSkipLP,
    useLastSearchedLocation,
    lastSearchedLocation,
    searchedLocation,
    weddingLocationForSRP,
  ]);

  const linkProps = openInNewTab
    ? {
        target: '_blank',
      }
    : {};

  return (
    <div
      className={cx(styles.categories, className, {
        [styles.gridAllBreakpoints]: isClpTestAllBreakpoints,
        [styles.grid]: isClpTest,
      })}
      {...rest}
      data-testid="category-links"
    >
      {categories.map((vendorTaxonomyKey) => {
        const url =
          isClpTest || shouldSkipLP
            ? vendorSearchUrl(vendorTaxonomyKey, locationToUse?.slug)
            : vendorCategoryLandingUrl(vendorTaxonomyKey);
        const name = getVendorWordPill(vendorTaxonomyKey);
        const showNewTag =
          vendorTaxonomyKey === OFFICIANTS_TAXONOMY_KEY ||
          vendorTaxonomyKey === EXTRAS_TAXONOMY_KEY;
        return (
          <LinkV2
            className={cx(styles.category, {
              [styles.categoryAllBreakpoints]: isClpTestAllBreakpoints,
            })}
            key={`explore-by-category-${vendorTaxonomyKey}`}
            href={url}
            subtle={!isClpTest}
            noUnderline
            data-testid="category-link"
            onClick={() => {
              onLinkClick();
            }}
            {...linkProps}
          >
            {isClpTestAllBreakpoints ? null : (
              <>
                <VendorCircleIcon
                  className={cx({ [styles.hideNotDesktop]: isClpTest })}
                  taxonomyKey={vendorTaxonomyKey}
                  size={64}
                />
                {showNewTag && (
                  <TagV2
                    className={cx(styles.new, { [styles.hideNotDesktop]: isClpTest })}
                    variant={TagV2Variant.SKY}
                  >
                    New
                  </TagV2>
                )}
              </>
            )}
            {isClpTest && (
              <img
                className={cx(styles.tile, {
                  [styles.hideDesktop]: !isClpTestAllBreakpoints,
                })}
                src={mapVendorKeyToTile(vendorTaxonomyKey)}
                alt={name}
              />
            )}
            {name}
          </LinkV2>
        );
      })}
    </div>
  );
};

export const VendorsByCategory = ({
  className,
  excludeCategoryByType,
  isClpTestAllBreakpoints = false,
  isClpTest = isClpTestAllBreakpoints,
  hideHed = false,
  showModal,
  useLastSearchedLocation = false,
}: VendorsByCategoryProps) => {
  const { searchedLocation } = useSearchedLocation();
  const { isMobile, isTablet } = useResponsiveDesign();
  const filteredCategories = ORDERED_VENDOR_CATEGORY_KEYS.filter(
    (vendorTaxonomyKey) => excludeCategoryByType !== vendorTaxonomyKey
  );

  const hed =
    isClpTest && (isMobile || isTablet) ? (
      <Title2 className={styles.hed} presentation="h3">
        Find vendors in{' '}
        <LinkV2 role="button" onClick={showModal} subtle>
          {searchedLocation?.label} <PencilIcon height={20} width={20} />
        </LinkV2>
      </Title2>
    ) : (
      <Title2 className={styles.hed} presentation="h3">
        Explore wedding vendors by category
      </Title2>
    );

  return (
    <div className={cx(styles.vendorsByCategory, className)}>
      {!hideHed && hed}
      <VendorsCategoryList
        categories={filteredCategories}
        isClpTest={isClpTest}
        isClpTestAllBreakpoints={isClpTestAllBreakpoints}
        useLastSearchedLocation={useLastSearchedLocation}
      />
    </div>
  );
};

export default VendorsByCategory;
